import type { AssistantPendingDTO, ConfirmTreeCard } from '@contracts/assistant';
import type { MissionCardValue, MissionDiffValue, MissionStepExecutor } from '@contracts/assistant-missions';
import type { Id } from '@contracts/common';
import type { BatchRowDTO, IngestJobDTO } from '@contracts/ingest';

/**
 * A pending action the Map draws as ghost cards (AI S1 spec §8.3.2, D206, D207): Hana's drafted tree, or a heavy move
 * — a move waits on a card only when it is heavy, a light one runs at once.
 */
/**
 * `line` names the draft across its narrowings: a ✕ stores the smaller draft under a new actionId (the edit
 * endpoint's `editActionId`), yet it is the same draft to the Map — its view, its folds and its reveal carry over.
 */
/**
 * A drafted card. A mission's (S7 §6.2) also says who does it — `goal` for the goal card — and the assignee's name, so
 * its ghost reads "Mục tiêu · hạn 31/10", "✦ Hana · 07/10" or "Chi · 14/10".
 */
export type DraftCard = ConfirmTreeCard & { executor?: MissionStepExecutor | 'goal'; assigneeName?: string | null };
export type S1Draft =
  | { kind: 'plan'; pending: AssistantPendingDTO; line: string; parentId: Id | null; cards: readonly DraftCard[];
    /** A `mission.create` plan: the Map's bar says "Kế hoạch nháp · N thẻ" (P18). */
    mission: boolean }
  | { kind: 'move'; pending: AssistantPendingDTO; line: string; id: Id; parentId: Id | null; afterId: Id | null | undefined };

/**
 * A ghost of a batch (S5 §10.1): a create task/note row, keyed by the row's id. A root names the card it hangs under
 * (`anchorId`, null for a loose card, "Việc lẻ"); a child names its parent row's id as `parentKey`.
 */
export type BatchDraftCard = ConfirmTreeCard & { anchorId: Id | null; row: BatchRowDTO };
/**
 * The ready batch the Map shows (`?batch`, D150, D151): its drawn cards (≤ `BATCH_DRAFT_MAX`, parents first), the
 * cards outlined as anchors (the roots' anchors and the update rows' targets), and every create row it would draw.
 */
export type BatchDraft = {
  kind: 'batch'; job: IngestJobDTO; line: string; cards: readonly BatchDraftCard[]; outlined: readonly Id[]; total: number;
};
export type DraftPending = S1Draft | BatchDraft;

const idOrNull = (value: unknown): Id | null => (typeof value === 'string' && value !== '' ? value : null);

/** The key of a mission's goal card in its draft: steps name it when they hang straight under the goal. */
export const GOAL_KEY = 'goal';

/** A mission's card value (D138), or null when the input carries none. */
export function missionOf(pending: AssistantPendingDTO): MissionCardValue | null {
  const value = pending.input.mission;
  return pending.capability === 'mission.create' && typeof value === 'object' && value !== null
    && Array.isArray((value as MissionCardValue).steps) ? value as MissionCardValue : null;
}

/** The `missionDiff` value of a pending `mission.revise` (D131), or null. */
export function missionDiffOf(pending: AssistantPendingDTO): MissionDiffValue | null {
  const value = pending.input.missionDiff;
  return pending.capability === 'mission.revise' && typeof value === 'object' && value !== null
    && Array.isArray((value as MissionDiffValue).add) && Array.isArray((value as MissionDiffValue).update)
    && Array.isArray((value as MissionDiffValue).remove) ? value as MissionDiffValue : null;
}

/** A mission as a tree draft: its goal card, then each step under the goal or under its parent step (parents first). */
function missionCards(value: MissionCardValue): DraftCard[] {
  const goal: DraftCard = { key: GOAL_KEY, parentKey: null, title: value.title, itemType: 'task', recipientId: null,
    deadline: value.deadline, executor: 'goal', assigneeName: null };
  const steps = value.steps.map((s): DraftCard => ({
    key: s.ref, parentKey: s.parentRef ?? GOAL_KEY, title: s.title, itemType: 'task', recipientId: s.assignee?.id ?? null,
    deadline: s.deadline, executor: s.executor, assigneeName: s.assignee?.name ?? null,
  }));
  const out: DraftCard[] = [goal];
  const placed = new Set([GOAL_KEY]);
  // The stored order is the model's; a child listed before its parent waits for it (a cycle is never drawn).
  let left = steps;
  while (left.length > 0) {
    const ready = left.filter((s) => placed.has(s.parentKey!));
    if (ready.length === 0) break;
    for (const s of ready) { out.push(s); placed.add(s.key); }
    left = left.filter((s) => !placed.has(s.key));
  }
  return out;
}

/** The draft a pending action carries, or null when it is not one the Map draws. */
export function draftOf(pending: AssistantPendingDTO): S1Draft | null {
  const input = pending.input;
  if (pending.capability === 'work.apply_plan' && Array.isArray(input.cards)) {
    return { kind: 'plan', pending, line: pending.actionId, parentId: idOrNull(input.parentId), cards: input.cards as ConfirmTreeCard[],
      mission: false };
  }
  const mission = missionOf(pending);
  if (mission !== null) {
    return { kind: 'plan', pending, line: pending.actionId, parentId: idOrNull(input.parentId), cards: missionCards(mission),
      mission: true };
  }
  if (pending.capability === 'work.move' && typeof input.id === 'string') {
    const afterId = 'afterId' in input ? idOrNull(input.afterId) : undefined;
    return { kind: 'move', pending, line: pending.actionId, id: input.id, parentId: idOrNull(input.parentId), afterId };
  }
  return null;
}

/**
 * "Xem trên bản đồ": the Map, and nothing more — the Map brings a new draft into view itself, and an `item=` would open
 * the parent's modal over the ghosts (P4 G7 review 4).
 */
export const DRAFT_MAP_HREF = '/workplace?view=map';

/** A card and every card below it in the draft, by key: what a ghost's ✕ takes away (D206). */
export function withDescendants(cards: readonly ConfirmTreeCard[], key: string): Set<string> {
  const out = new Set([key]);
  // Cards come parents first (the stored schema's order), so one pass reaches every depth.
  for (const c of cards) if (c.parentKey !== null && out.has(c.parentKey)) out.add(c.key);
  return out;
}

/** D151: the most ghosts one batch draws on the Map. */
export const BATCH_DRAFT_MAX = 50;

/** Where a drawn row hangs: under another drawn row, or under a card (null: a loose top-level card). */
type Place = { parent: BatchRowDTO } | { anchorId: Id | null };

/**
 * A ready batch as a Map draft (§10.1, D151): its included create task/note rows. A row naming a card hangs under it;
 * a row naming another row hangs under that row's ghost, or under its card when that row updates one; a row naming
 * nothing hangs under the job's default parent, else its anchor, else nowhere (Việc lẻ). A row whose parent row is not
 * drawn is not drawn either (D93). Update rows draw no ghost: their cards are outlined instead.
 */
export function draftOfBatch(job: IngestJobDTO, rows: readonly BatchRowDTO[]): BatchDraft {
  const bySeq = new Map(rows.map((r) => [r.seq, r]));
  const live = (r: BatchRowDTO): boolean => r.included && r.status !== 'rejected' && (r.kind === 'task' || r.kind === 'note');
  const loose = job.defaults.parentItemId ?? job.anchorItemId ?? null;
  const places = new Map<Id, Place | null>();
  const placeOf = (r: BatchRowDTO): Place | null => {
    if (places.has(r.id)) return places.get(r.id)!;
    places.set(r.id, null); // a cycle draws nothing
    let place: Place | null = null;
    const ref = r.parentRef;
    if (!live(r) || r.op !== 'create') place = null;
    else if (ref === null) place = { anchorId: loose };
    else if ('itemId' in ref) place = { anchorId: ref.itemId };
    else {
      const parent = bySeq.get(ref.seq);
      if (parent === undefined || !live(parent)) place = null;
      else if (parent.op === 'update') place = parent.targetId === null ? null : { anchorId: parent.targetId };
      else if (placeOf(parent) !== null) place = { parent };
    }
    places.set(r.id, place);
    return place;
  };
  const sorted = [...rows].sort((a, b) => a.seq - b.seq);
  const drawn = sorted.filter((r) => placeOf(r) !== null);
  // Parents first, each followed by its own rows (the card's order), so a cap never keeps a child without its parent.
  const ordered: BatchRowDTO[] = [];
  const visit = (r: BatchRowDTO) => {
    ordered.push(r);
    for (const c of drawn) { const pl = places.get(c.id)!; if ('parent' in pl && pl.parent.id === r.id) visit(c); }
  };
  for (const r of drawn) if ('anchorId' in places.get(r.id)!) visit(r);
  const cards = ordered.slice(0, BATCH_DRAFT_MAX).map((row): BatchDraftCard => {
    const place = places.get(row.id)!;
    const text = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
    return {
      key: row.id, parentKey: 'parent' in place ? place.parent.id : null, anchorId: 'anchorId' in place ? place.anchorId : null,
      title: row.view.title, itemType: row.kind === 'note' ? 'note' : 'task', recipientId: text(row.fields.assigneeId),
      deadline: text(row.fields.deadline), row,
    };
  });
  const outlined = new Set<Id>();
  for (const c of cards) if (c.parentKey === null && c.anchorId !== null) outlined.add(c.anchorId);
  for (const r of sorted) if (live(r) && r.op === 'update' && r.targetId !== null) outlined.add(r.targetId);
  return { kind: 'batch', job, line: `batch:${job.id}`, cards, outlined: [...outlined], total: drawn.length };
}
